import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Header } from "../../components";
import { useStateContext } from "../../contexts/ContextProvider";
import { MdAdd, MdViewAgenda, MdViewHeadline } from 'react-icons/md';

import WatchListTabs from "./WatchListTabs";
import WatchListTable from "./WatchListTable";

import InstrumentSelector from "../../components/common/InstrumentSelector";
import ContinuousContractModal from "../../components/common/ContinuousContractModal";
import ConfirmationModal from "../../components/common/ConfirmationModal";

import { SUPPORTED_COUNTRIES, CURRENCY_CONFIG } from '../../class/GlobalVars';
import { services } from '../../services/api';
import { useWebSocket } from '../../hooks/useWebSocket';
import Loader from "../../components/Loader";
import { PageHeader, Toolbar, Segmented, Button } from "../../components/ds";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import MobileWatchList from "../../components/mobile/MobileWatchList";
import MobileInstrumentSearch from "../../components/mobile/MobileInstrumentSearch";

function WatchList() {
  const { setActiveMenu } = useStateContext();
  const { isPhone } = useBreakpoint();

  // Country Constraints - Imported from GlobalVars
  const COUNTRIES = SUPPORTED_COUNTRIES;

  //--> State
  const [filterCountry, setFilterCountry] = useState('ALL');
  const [filterType, setFilterType] = useState('ALL'); // 'ALL' | 'EQUITY' | 'FUT' | 'OPT'
  const [viewMode, setViewMode] = useState('compact'); // 'card' | 'compact'
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [pendingInstrument, setPendingInstrument] = useState(null);
  const [instrumentToDelete, setInstrumentToDelete] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null); // whole-list delete → named confirm
  const [resolvedMap, setResolvedMap] = useState({}); // { [original_id]: resolved_instrument_obj }
  
  //! Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  //--> Additional State for Previous Close and Trading Symbols
  const [activeAccountId, setActiveAccountId] = useState(null);
  const [activeBrokerName, setActiveBrokerName] = useState(null);  // e.g., "flattrade", "icici"
  const [previousCloseMap, setPreviousCloseMap] = useState({});
  const [tradingSymbolsMap, setTradingSymbolsMap] = useState({});  // { instrument_id: { broker_symbol, broker_token } }

  //--> WebSocket for live LTP updates
  const { subscribeTopic, isConnected } = useWebSocket({ 
    page_id: 'watchlist',
    autoConnect: true 
  });
  
  // Track active subscriptions
  const subscriptionsRef = useRef([]);

  //--> Fetch Active Broker Account
  useEffect(() => {
    const fetchActiveAccount = async () => {
      try {
        const response = await services.brokerAccounts.getAccounts();
        if (response && response.data) {
          // API returns direct list of accounts
          const accounts = Array.isArray(response.data) ? response.data : (response.data.data || []);
          
          // Find active session - check session_status which reflects live connection
          // Prefer Flattrade or ICICI if available
          const activeSessions = accounts.filter(a => a.session_status === 'active');
          
          const active = activeSessions.find(a => (a.broker_display_name || '').toLowerCase().includes('flattrade'))
                      || activeSessions.find(a => (a.broker_display_name || '').toLowerCase().includes('icici'))
                      || activeSessions[0];

          if (active) {
              console.log(`[WatchList] Using active broker account: ${active.broker_display_name} (${active.account_id})`);
              setActiveAccountId(active.account_id);
              // Extract broker name for trading symbols lookup (normalize to lowercase)
              const brokerName = (active.broker_display_name || '').toLowerCase().replace(/\s+/g, '').split('(')[0];
              setActiveBrokerName(brokerName);
          } else {
              console.log('[WatchList] No active broker sessions found for market info');
          }
        }
      } catch (err) {
        console.error('Failed to fetch broker accounts:', err);
      }
    };
    fetchActiveAccount();
  }, []);

  //--> Fetch watchlists from backend on mount
  const fetchWatchlists = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await services.watchlist.getAll();
      const data = response.data || [];
      
      const initialResolvedMap = {};

      // Transform backend data to match frontend structure
      const transformed = data.map(wl => ({
        id: wl.id,
        name: wl.name,
        display_order: wl.display_order,
        instruments: (wl.items || []).map(item => {
          // Pre-seed resolvedMap for client-side fallback (e.g., mid-session rollovers)
          // Note: Primary resolution now happens via direct field overwrite below
          if (item.is_continuous && item.resolved_instrument_id) {
              initialResolvedMap[item.instrument_id] = {
                  instrument_id: item.resolved_instrument_id,
                  contract: {
                      instrument_id: item.resolved_instrument_id,
                      symbol: item.resolved_symbol,
                      expiry_date: item.resolved_expiry_date,
                      days_to_expiry: item.days_to_expiry,
                      name: item.resolved_name || item.name,
                      exchange: item.exchange
                  }
              };
          }

          const isResolved = item.is_continuous && item.resolved_instrument_id;
          
          return {
            ...item,
            // If resolved by backend, OVERWRITE core fields to prevent UI flash
            instrument_id: isResolved ? item.resolved_instrument_id : item.instrument_id,
            symbol: isResolved ? (item.resolved_symbol || item.symbol) : item.symbol,
            name: isResolved ? (item.resolved_name || item.name) : item.name,
            expiry_date: isResolved ? (item.resolved_expiry_date || item.expiry_date) : item.expiry_date,
            
            // Keep original ID for reference logic (e.g. resolvedMap lookups)
            base_instrument_id: item.instrument_id,
            
            // Initialize LTP fields for live updates
            ltp: item.ltp || null,
            change_percent: item.change_percent || 0,
            change_value: item.change_value || 0,
            strategies_count: 0,
            // Country/Currency from exchange
            country_code: item.exchange?.includes('NSE') || item.exchange?.includes('BSE') || item.exchange?.includes('NFO') || item.exchange?.includes('MCX') ? 'IN' : 'US',
            currency: item.exchange?.includes('NSE') || item.exchange?.includes('BSE') || item.exchange?.includes('NFO') || item.exchange?.includes('MCX') ? 'INR' : 'USD',
          };
        })
      }));
      
      // Update resolvedMap state first to ensure no "Jan" flash
      if (Object.keys(initialResolvedMap).length > 0) {
          console.log(`[WatchList] Pre-seeded ${Object.keys(initialResolvedMap).length} resolved contracts from backend`);
          setResolvedMap(prev => ({ ...prev, ...initialResolvedMap }));
      }

      setGroups(transformed);
      
      // Set active group to first one if not set
      if (transformed.length > 0 && !activeGroupId) {
        setActiveGroupId(transformed[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch watchlists:', err);
      setError('Failed to load watchlists');
    } finally {
      setLoading(false);
    }
  }, [activeGroupId]);

  useEffect(() => {
    setActiveMenu(false);
    fetchWatchlists();
  }, []);

  //--> Ref to hold latest resolvedMap for callbacks without triggering re-renders
  const resolvedMapRef = useRef(resolvedMap);
  resolvedMapRef.current = resolvedMap;

  //--> Extract stable instrument metadata (IDs only, not prices)
  // This only changes when instruments are added/removed, not when prices update
  const instrumentMetadata = useMemo(() => {
    const allInstruments = groups.flatMap(g => g.instruments || []);
    const metadata = allInstruments.map(inst => ({
      // Use NO FLASH ID if available, otherwise original
      id: inst.instrument_id, // Already overwritten in fetchWatchlists if resolved
      symbol: inst.symbol,
      //! V12B-11: carried so each tick subscription can declare its venue. Without it
      //! the delivery-stall watchdog can only ask "is ANY Indian venue open", and an
      //! NSE-only watchlist reads its own correct post-15:30 silence as a fault until
      //! MCX shuts at 23:30. Part of the metadata string, so adding or removing an
      //! instrument re-fingerprints and the subscriptions are rebuilt with it.
      exchange: inst.exchange,
      isContinuous: inst.is_continuous || false
    }));
    
    // Create a stable string representation for comparison
    const metadataString = JSON.stringify(metadata);
    // console.log('[WatchList] 📋 Instrument metadata updated:', metadata.length, 'instruments');
    return { metadata, metadataString };
  }, [groups]);

  //--> Calculate stable fingerprint for subscriptions
  // This uses the metadata string, so it only recalculates when instruments change
  const subscriptionFingerprint = useMemo(() => {
    const { metadata } = instrumentMetadata;
    const uniqueIds = new Set();

    metadata.forEach(inst => {
      let targetId = inst.id; 
      
      // FALLBACK: Use the current resolvedMap (for client-side rollovers/resolutions)
      const lookupId = inst.base_instrument_id || inst.id;
      if (inst.isContinuous && resolvedMap[lookupId]) {
        const resolved = resolvedMap[lookupId];
        const resolvedId = resolved.instrument_id || resolved.contract?.instrument_id;
        if (resolvedId) targetId = resolvedId;
      }
      
      if (targetId) uniqueIds.add(targetId);
    });
    
    const fingerprint = Array.from(uniqueIds).sort().join(',');
    return fingerprint;
  }, [instrumentMetadata.metadataString, resolvedMap]);

  //--> V12B-11: targetId -> venue, for the delivery-stall watchdog.
  //*   Derived by the SAME rule as the fingerprint above — including continuous-contract
  //*   resolution — because a target id whose exchange came from a different derivation
  //*   would silently mis-gate that row's silence. One rule, applied twice.
  const exchangeByTarget = useMemo(() => {
    const { metadata } = instrumentMetadata;
    const map = {};

    metadata.forEach(inst => {
      let targetId = inst.id;
      let exchange = inst.exchange;

      const lookupId = inst.base_instrument_id || inst.id;
      if (inst.isContinuous && resolvedMap[lookupId]) {
        const resolved = resolvedMap[lookupId];
        const resolvedId = resolved.instrument_id || resolved.contract?.instrument_id;
        if (resolvedId) targetId = resolvedId;
        //! the resolved CONTRACT's venue wins — a continuous NIFTY pin resolves to an
        //! NFO contract, and gating it on the base row's exchange would be wrong.
        exchange = resolved.exchange || resolved.contract?.exchange || exchange;
      }

      if (targetId && exchange) map[targetId] = exchange;
    });

    return map;
  }, [instrumentMetadata.metadataString, resolvedMap]); // eslint-disable-line react-hooks/exhaustive-deps

  //--> Ref so the subscribe effect can read it without re-subscribing on identity change
  const exchangeByTargetRef = useRef(exchangeByTarget);
  exchangeByTargetRef.current = exchangeByTarget;

  //--> Fetch Previous Close Prices
  // Triggered when groups change or we get an active account
  useEffect(() => {
      if (!activeAccountId || !instrumentMetadata.metadataString) return;

      const fetchPreviousClose = async () => {
          const allInstruments = groups.flatMap(g => g.instruments || []);
          if (allInstruments.length === 0) return;

          // Prepare list for API - handle continuous contracts resolution
          // We need to send the ACTUAL underlying instrument details that define the contract
          // If resolved, send resolution. If normal, send normal.
          const payloadInstruments = allInstruments.map(inst => {
            if (inst.is_continuous && resolvedMap[inst.instrument_id]) {
                return { ...inst, ...resolvedMap[inst.instrument_id] };
            }
            return inst;
          });

          // Deduplicate
          const uniqueInstruments = [];
          const seenIds = new Set();
          
          payloadInstruments.forEach(inst => {
              if (!seenIds.has(inst.instrument_id)) {
                  seenIds.add(inst.instrument_id);
                  
                  // Determine product type safer
                  let productType = 'cash';
                  if (inst.instrument_type) {
                      const type = inst.instrument_type.toUpperCase();
                      if (type.includes('FUT')) productType = 'futures';
                      else if (type.includes('OPT')) productType = 'options';
                  }

                  // Get broker-specific symbol if available from tradingSymbolsMap
                  const tradingInfo = tradingSymbolsMap[inst.instrument_id] || {};
                  
                  uniqueInstruments.push({
                      instrument_id: inst.instrument_id,
                      exchange: inst.exchange,
                      symbol: tradingInfo.broker_symbol || inst.symbol,  // Use broker_symbol if available
                      broker_token: tradingInfo.broker_token || inst.broker_token || inst.token,
                      product_type: productType,
                      expiry_date: inst.expiry_date || null,
                      strike_price: inst.strike_price ? String(inst.strike_price) : "0",
                      right: inst.option_type === 'CE' ? 'call' : (inst.option_type === 'PE' ? 'put' : 'others')
                  });
              }
          });

          if (uniqueInstruments.length === 0) return;

          try {
              console.log(`[WatchList] Fetching previous close for ${uniqueInstruments.length} instruments...`);
              const response = await services.brokerAccounts.getPreviousClose(activeAccountId, uniqueInstruments);
              if (response.data && response.data.data && response.data.data.prices) {
                  console.log(`[WatchList] ✅ Received ${Object.keys(response.data.data.prices).length} previous close prices`);
                  setPreviousCloseMap(prev => ({ ...prev, ...response.data.data.prices }));
              } else {
                  console.warn('[WatchList] ⚠️ Previous close response invalid format:', response.data);
              }
          } catch (err) {
              console.error("Failed to fetch previous close prices", err);
          }
      };

      // Simple implementation: wait a moment for resolutions to settle
      const timer = setTimeout(() => {
          fetchPreviousClose();
      }, 1000); 
      
      return () => clearTimeout(timer);
  }, [activeAccountId, instrumentMetadata.metadataString, resolvedMap, tradingSymbolsMap]);

  //--> Fetch Trading Symbols (broker_symbol, broker_token) for all instruments
  // This runs after resolvedMap is populated, to get broker-specific symbols
  useEffect(() => {
    const fetchTradingSymbols = async () => {
      // Use ref to access latest groups without triggering re-run
      // Actually, we can just use groups here because effect only triggers on metadataString change
      // But we need to be careful. Ideally we'd validly depend on groups but filter execution.
      // For now, depending on metadataString is safer for loop prevention.
      if (!activeBrokerName || groups.length === 0) return;
      
      // Collect all instrument IDs (using resolved IDs for continuous contracts)
      const allInstruments = groups.flatMap(g => g.instruments || []);
      const instrumentIds = allInstruments.map(inst => {
        if (inst.is_continuous && resolvedMap[inst.instrument_id]?.contract?.instrument_id) {
          return resolvedMap[inst.instrument_id].contract.instrument_id;
        }
        return inst.instrument_id;
      }).filter(Boolean);
      
      if (instrumentIds.length === 0) return;
      
      // Deduplicate
      const uniqueIds = [...new Set(instrumentIds)];
      
      try {
        // console.log(`[WatchList] Fetching trading symbols for ${uniqueIds.length} instruments (broker: ${activeBrokerName})`);
        const response = await services.instrumentMaster.getTradingSymbols(uniqueIds, activeBrokerName);
        
        if (response.data?.instruments) {
          const newMap = {};
          response.data.instruments.forEach(inst => {
            if (inst.broker_mappings && inst.broker_mappings.length > 0) {
              // Take first mapping (already filtered by broker_name on backend)
              const mapping = inst.broker_mappings[0];
              newMap[inst.instrument_id] = {
                broker_symbol: mapping.broker_symbol,
                broker_token: mapping.broker_token
              };
            }
          });
          // check if map actually changed to avoid loop
          setTradingSymbolsMap(prev => {
             const changed = Object.keys(newMap).some(k => 
                !prev[k] || prev[k].broker_symbol !== newMap[k].broker_symbol
             );
             if (!changed && Object.keys(newMap).length === Object.keys(prev).filter(k => newMap[k]).length) return prev;
             
             console.log(`[WatchList] ✅ Loaded trading symbols for ${Object.keys(newMap).length} instruments`);
             return { ...prev, ...newMap };
          });
        }
      } catch (err) {
        console.error('[WatchList] Failed to fetch trading symbols:', err);
      }
    };
    
    // Delay to allow resolutions to settle first
    const timer = setTimeout(fetchTradingSymbols, 500);
    return () => clearTimeout(timer);
  }, [activeBrokerName, instrumentMetadata.metadataString, resolvedMap]);


  //--> Resolve Continuous Contracts
  useEffect(() => {
    const resolveContracts = async () => {
      // 1. Identify continuous instruments that need resolution
      const instrumentsToResolve = [];
      
      groups.forEach(g => {
        if (Array.isArray(g.instruments)) {
          g.instruments.forEach(i => {
            if (i.is_continuous && !resolvedMap[i.instrument_id]) {
               instrumentsToResolve.push(i);
            }
          });
        }
      });

      if (instrumentsToResolve.length === 0) return;

      console.log(`[WatchList] Resolving ${instrumentsToResolve.length} continuous contracts`);

      // 2. Resolve them
      const newResolutions = {};
      await Promise.all(instrumentsToResolve.map(async (inst) => {
        try {
          // Use the correct API method from instrumentMaster
          const response = await services.instrumentMaster.getActiveContract(
            inst.symbol, 
            inst.exchange,
            inst.rollover_days_before_expiry
          );
          
          if (response.data) {
             newResolutions[inst.instrument_id] = response.data;
          }
        } catch (err) {
          console.warn(`Failed to resolve contract for ${inst.symbol}:`, err);
        }
      }));

      // 3. Update state if we found new resolutions
      if (Object.keys(newResolutions).length > 0) {
        setResolvedMap(prev => ({ ...prev, ...newResolutions }));
      }
    };

    resolveContracts();
  }, [instrumentMetadata.metadataString, resolvedMap]);

  // Track the previous fingerprint to detect actual changes
  const prevFingerprintRef = useRef('');

  //--> Ref to hold latest previousCloseMap for callbacks
  const previousCloseMapRef = useRef(previousCloseMap);
  previousCloseMapRef.current = previousCloseMap;

  //--> WebSocket subscription for live LTP updates
  useEffect(() => {
    console.log(`[WatchList] 🏁 Effect triggered. Connected: ${isConnected}, Fingerprint: ${subscriptionFingerprint}, Prev: ${prevFingerprintRef.current}`);
    
    if (!isConnected || !subscriptionFingerprint) {
        // ... (existing logic)
        if (!isConnected) {
          prevFingerprintRef.current = null;
        }
        return;
    }

    // Check if fingerprint actually changed
    if (prevFingerprintRef.current === subscriptionFingerprint) {
      return;
    }
    
    console.log('[WatchList] 🔄 Fingerprint changed from', prevFingerprintRef.current, 'to', subscriptionFingerprint);
    console.log('[WatchList] ✅ Subscribing to new fingerprint:', subscriptionFingerprint);
    prevFingerprintRef.current = subscriptionFingerprint;

    // Cleanup previous subscriptions
    subscriptionsRef.current.forEach(unsub => {
      if (typeof unsub === 'function') unsub();
    });
    subscriptionsRef.current = [];
    
    const targetIds = subscriptionFingerprint.split(',').filter(Boolean);
    
    targetIds.forEach(targetId => {
       const topic = `market.${targetId}.tick`;
       
       // Subscribe
       //! V12B-11: declare the venue so the delivery-stall watchdog judges THIS row's
       //! silence against ITS exchange's hours. Absent => the watchdog falls back to
       //! the all-venues union, i.e. exactly the pre-V12B-11 behaviour.
       const unsub = subscribeTopic(topic, (msg) => {
         // ... (handler logic same as before)
         const tickData = msg.data || msg;
         const tickId = targetId; 
         const currentResolvedMap = resolvedMapRef.current;
         const currentPreviousCloseMap = previousCloseMapRef.current;

         setGroups(prev => prev.map(g => ({
           ...g,
           instruments: g.instruments.map(i => {
             // ... (match logic same as before)
             let isMatch = i.instrument_id === tickId;
             if (!isMatch && i.is_continuous && currentResolvedMap[i.instrument_id]) {
                isMatch = currentResolvedMap[i.instrument_id].instrument_id === tickId;
             }
             if (isMatch) {
                const newLtp = tickData.ltp || tickData.last_price;
                if (!newLtp) return i; // Skip bad ticks

                // Calculate Change relative to Previous Close
                const prevClose = currentPreviousCloseMap[i.instrument_id] || i.close || i.ltp; // Fallback to current if no prev
                
                let change = 0;
                let changePct = 0;
                
                if (prevClose && prevClose > 0) {
                    change = newLtp - prevClose;
                    changePct = (change / prevClose) * 100;
                }

                // Merge resolved contract details if continuous
                const resolvedData = i.is_continuous && currentResolvedMap[i.instrument_id]?.contract;
                const updatedInstrument = resolvedData ? {
                    ...i,
                    // Override key fields with resolved contract details
                    symbol: resolvedData.symbol,
                    expiry_date: resolvedData.expiry_date,
                    instrument_id: resolvedData.instrument_id,
                    exchange_token: resolvedData.exchange_token,
                    days_to_expiry: resolvedData.days_to_expiry
                } : i;

                return { 
                    ...updatedInstrument, 
                    ltp: newLtp, 
                    change_value: change, 
                    change_percent: changePct 
                };
             }
             return i;
           })
         })));
       }, { exchange: exchangeByTargetRef.current[targetId] });

       subscriptionsRef.current.push(unsub);
    });
    
    return () => {
      // Cleanup logic
      subscriptionsRef.current.forEach(unsub => {
        if (typeof unsub === 'function') unsub();
      });
      subscriptionsRef.current = [];
    };
  }, [isConnected, subscriptionFingerprint, subscribeTopic]);

  // Computed
  // Facet by instrument type (equity vs futures vs options). Instrument types
  // vary (EQUITY / FUTURE / FUT / OPTION / OPT / FUTIDX …) so match by substring.
  const matchesType = (inst) => {
    if (filterType === 'ALL') return true;
    const t = (inst.instrument_type || 'EQUITY').toUpperCase();
    if (filterType === 'EQUITY') return !t.includes('FUT') && !t.includes('OPT');
    if (filterType === 'FUT') return t.includes('FUT');
    if (filterType === 'OPT') return t.includes('OPT');
    return true;
  };

  const getCurrentGroupInstruments = () => {
    const group = groups.find(g => g.id === activeGroupId);
    if (!group) return [];

    // Filter by Country (preserve prior behaviour: ALL returns as-is, otherwise
    // exchange-filter + merge resolved continuous-contract details for display).
    let result;
    if (filterCountry === 'ALL') {
      result = group.instruments;
    } else {
      const targetExchanges = COUNTRIES.find(c => c.code === filterCountry)?.flags || [];
      const filtered = group.instruments.filter(i => targetExchanges.includes(i.exchange));
      result = filtered.map(inst => {
      // 1. Try resolvedMap (client-side updates/rollovers)
      // The map is keyed by the BASE ID (database instrument_id)
      const lookupId = inst.base_instrument_id || inst.instrument_id;
      const resolvedFromMap = resolvedMap[lookupId]?.contract;

      if (inst.is_continuous && resolvedFromMap) {
         return {
            ...inst,
            symbol: resolvedFromMap.symbol || inst.symbol,
            expiry_date: resolvedFromMap.expiry_date || inst.expiry_date,
            instrument_id: resolvedFromMap.instrument_id || inst.instrument_id,
            exchange_token: resolvedFromMap.exchange_token || inst.exchange_token,
            days_to_expiry: resolvedFromMap.days_to_expiry ?? inst.days_to_expiry,
            active_expiry_date: resolvedFromMap.expiry_date || inst.expiry_date,
            name: resolvedFromMap.name || inst.name
         };
      }
      
      // Otherwise, use the instrument as is (already pre-seeded in fetchWatchlists)
      return inst;
      });
    }

    // Filter by instrument type (applies in both country paths)
    return result.filter(matchesType);
  };

  //--> Handlers - Create Group
  const handleCreateGroup = async (name) => {
    try {
      const response = await services.watchlist.create({ name: name.toUpperCase() });
      const newWatchlist = response.data;
      
      const newGroup = {
        id: newWatchlist.id,
        name: newWatchlist.name,
        display_order: newWatchlist.display_order,
        instruments: []
      };
      
      setGroups([...groups, newGroup]);
      setActiveGroupId(newGroup.id);
    } catch (err) {
      console.error('Failed to create watchlist:', err);
    }
  };

  //--> Handlers - Rename Group
  const handleRenameGroup = async (id, newName) => {
    try {
      await services.watchlist.update(id, { name: newName.toUpperCase() });
      setGroups(groups.map(g => (g.id === id ? { ...g, name: newName.toUpperCase() } : g)));
    } catch (err) {
      console.error('Failed to rename watchlist:', err);
    }
  };

  //--> Handlers - Delete Group
  const handleDeleteGroup = async (id) => {
    try {
      await services.watchlist.delete(id);
      const newGroups = groups.filter(g => g.id !== id);
      setGroups(newGroups);
      if (activeGroupId === id) {
        setActiveGroupId(newGroups.length > 0 ? newGroups[0].id : null);
      }
    } catch (err) {
      console.error('Failed to delete watchlist:', err);
    }
  };

  //--> Handlers - Add Instrument (pre-modal)
  const handleAddInstrument = (payload) => {
    // Handle both direct instrument object and InstrumentSelector payload wrapper
    const instrument = payload.instrument || payload;
    
    // Check if it's F&O to trigger Continuous Modal
    const isFnO = ['FUTURE', 'OPTION', 'FUT', 'OPT'].includes(
      instrument.instrument_type?.toUpperCase()
    );

    if (isFnO) {
      setPendingInstrument(instrument);
      setIsSelectorOpen(false);
    } else {
      confirmAddInstrument({ instrument, is_continuous: false });
      setIsSelectorOpen(false);
    }
  };

  //--> Handlers - Confirm Add Instrument (after modal)
  const confirmAddInstrument = async (config) => {
    const { instrument, is_continuous, rollover_days_before_expiry, active_expiry_date } = config;

    // Check if already exists
    const currentGroup = groups.find(g => g.id === activeGroupId);
    if (currentGroup?.instruments.some(i => i.instrument_id === instrument.instrument_id)) {
      setPendingInstrument(null);
      return;
    }

    try {
      const type = instrument.instrument_type || 'EQUITY';
      const isOpt = type.toUpperCase().includes('OPT');
      
      // Build payload for API
      const payload = {
        instrument_id: instrument.instrument_id,
        symbol: instrument.symbol,
        exchange: instrument.exchange,
        instrument_type: type,
        name: instrument.name || instrument.description,
        strike_price: isOpt ? instrument.strike_price : null,
        option_type: isOpt ? instrument.option_type : null,
        expiry_date: instrument.expiry_date || active_expiry_date || null,
        is_continuous,
        rollover_days_before_expiry: rollover_days_before_expiry || null,
        active_expiry_date: active_expiry_date || null
      };
      
      const response = await services.watchlist.addItem(activeGroupId, payload);
      const newItem = response.data;
      
      // Determine Country & Currency
      const countryCode = instrument.country || instrument.country_code || 'IN';
      let currency = instrument.currency;
      if (!currency) {
        if (CURRENCY_CONFIG[countryCode]) {
          currency = CURRENCY_CONFIG[countryCode].code;
        } else {
          currency = 'INR';
        }
      }

      let rolloverDate = null;
      if (is_continuous && active_expiry_date) {
        const d = new Date(active_expiry_date);
        d.setDate(d.getDate() - (rollover_days_before_expiry || 3));
        rolloverDate = d.toISOString().split('T')[0];
      }

      // Add to local state
      const newInstrument = {
        ...newItem,
        ...instrument,
        id: newItem.id,
        ltp: null,
        change_percent: 0,
        change_value: 0,
        strategies_count: 0,
        country_code: countryCode,
        currency: currency,
        next_rollover_date: rolloverDate
      };
      
      setGroups(groups.map(g => {
        if (g.id === activeGroupId) {
          return { ...g, instruments: [...g.instruments, newInstrument] };
        }
        return g;
      }));
      
    } catch (err) {
      console.error('Failed to add instrument:', err);
    }
    
    setPendingInstrument(null);
  };

  //--> Handlers - Remove Instrument (trigger confirmation)
  const handleRemoveInstrument = (instrumentId) => {
    setInstrumentToDelete(instrumentId);
  };

  //--> Handlers - Execute Delete
  const executeDeleteInstrument = async () => {
    if (!instrumentToDelete) return;

    // Find the item in current group
    const currentGroup = groups.find(g => g.id === activeGroupId);
    const item = currentGroup?.instruments.find(i => i.instrument_id === instrumentToDelete);
    
    if (!item?.id) {
      setInstrumentToDelete(null);
      return;
    }

    try {
      await services.watchlist.removeItem(activeGroupId, item.id);
      
      setGroups(groups.map(g => {
        if (g.id === activeGroupId) {
          return {
            ...g,
            instruments: g.instruments.filter(i => i.instrument_id !== instrumentToDelete)
          };
        }
        return g;
      }));
    } catch (err) {
      console.error('Failed to remove instrument:', err);
    }
    
    setInstrumentToDelete(null);
  };

  //--> Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10 flex items-center justify-center">
        <Loader text="Loading watchlists..." />
      </div>
    );
  }

  // Phone Watch List → native-feel scoped-pills + instrument-row list.
  // The AppShell MobileHeader owns the title; modals below stay shared.
  if (isPhone) {
    return (
      <div className="min-h-screen">
        <MobileWatchList
          instruments={getCurrentGroupInstruments()}
          groups={groups}
          activeGroupId={activeGroupId}
          onSelectGroup={setActiveGroupId}
          filterType={filterType}
          onFilterType={setFilterType}
          onAdd={activeGroupId && groups.length > 0 ? () => setIsSelectorOpen(true) : null}
          onRemove={handleRemoveInstrument}
          onRenameGroup={handleRenameGroup}
          onDeleteGroup={(id) => setGroupToDelete(groups.find((g) => g.id === id) || null)}
          onCreateGroup={handleCreateGroup}
        />

        {/* Native full-screen instrument search (phone) — reuses the same
            onSelect contract, so the F&O / continuous-contract flow below is
            unchanged. */}
        <MobileInstrumentSearch
          open={isSelectorOpen}
          onClose={() => setIsSelectorOpen(false)}
          onSelect={handleAddInstrument}
        />
        <ContinuousContractModal
          isOpen={!!pendingInstrument}
          onClose={() => setPendingInstrument(null)}
          onConfirm={confirmAddInstrument}
          instrument={pendingInstrument}
        />
        <ConfirmationModal
          isOpen={!!instrumentToDelete}
          onClose={() => setInstrumentToDelete(null)}
          onConfirm={executeDeleteInstrument}
          title="Remove Instrument"
          message="Are you sure you want to remove this instrument from your watchlist?"
          confirmText="Remove"
          type="danger"
        />
        <ConfirmationModal
          isOpen={!!groupToDelete}
          onClose={() => setGroupToDelete(null)}
          onConfirm={() => { if (groupToDelete) handleDeleteGroup(groupToDelete.id); setGroupToDelete(null); }}
          title="Delete watchlist"
          message={`Delete the list “${groupToDelete?.name}” and all ${groupToDelete?.instruments?.length || 0} instrument(s) in it? This cannot be undone.`}
          confirmText="Delete list"
          type="danger"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10 transition-colors duration-300">

      {/* Standardized page header (common across all pages) */}
      <PageHeader
        title="Watchlist"
        subtitle="Real-time monitoring & algo execution status"
        actions={
          <div className="flex items-center gap-2">
            <Segmented
              aria-label="Country filter"
              value={filterCountry}
              onChange={setFilterCountry}
              options={COUNTRIES.map((c) => ({ value: c.code, label: c.label }))}
            />
            <Segmented
              aria-label="View mode"
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'card', label: 'Cards', icon: <MdViewAgenda size={14} /> },
                { value: 'compact', label: 'Compact', icon: <MdViewHeadline size={14} /> },
              ]}
            />
          </div>
        }
      />

      {/* Main Content Area */}
      <div className="flex flex-col gap-6 w-full">
         {/* Slot 2 · Controls (DS Toolbar) — group tabs (nav) + the tab-scoped
             "Add instrument" CTA (adds to the currently-open group). The
             page-global country filter lives in the PageHeader actions (Tier 1). */}
         <Toolbar
            nav={
              <WatchListTabs
                 groups={groups}
                 selectedGroupId={activeGroupId}
                 onSelectGroup={setActiveGroupId}
                 onCreateGroup={handleCreateGroup}
                 onRenameGroup={handleRenameGroup}
                 onDeleteGroup={(id) => setGroupToDelete(groups.find((g) => g.id === id) || null)}
              />
            }
            filters={
              <Segmented
                 aria-label="Instrument type"
                 value={filterType}
                 onChange={setFilterType}
                 options={[
                   { value: 'ALL', label: 'All' },
                   { value: 'EQUITY', label: 'Equity' },
                   { value: 'FUT', label: 'Futures' },
                   { value: 'OPT', label: 'Options' },
                 ]}
              />
            }
            actions={
              activeGroupId && groups.length > 0 ? (
                <Button
                   variant="accent"
                   onClick={() => setIsSelectorOpen(true)}
                   aria-label="Add instrument"
                >
                   <MdAdd className="text-base" />
                   <span className="hidden md:inline">Add instrument</span>
                </Button>
              ) : null
            }
         />

         {/* Content View */}
         <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
            <WatchListTable
               instruments={getCurrentGroupInstruments()}
               previousCloseMap={previousCloseMap}
               onRemoveInstrument={handleRemoveInstrument}
               viewMode={viewMode}
            />
         </div>
      </div>

      {/* Modal Integration */}
      {isSelectorOpen && (
        <InstrumentSelector
          open={isSelectorOpen}
          onClose={() => setIsSelectorOpen(false)}
          onSelect={handleAddInstrument}
          title="ADD TO WATCHLIST"
        />
      )}

      {/* Continuous Contract Modal */}
      <ContinuousContractModal 
        isOpen={!!pendingInstrument}
        onClose={() => setPendingInstrument(null)}
        onConfirm={confirmAddInstrument}
        instrument={pendingInstrument}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!instrumentToDelete}
        onClose={() => setInstrumentToDelete(null)}
        onConfirm={executeDeleteInstrument}
        title="Remove Instrument"
        message="Are you sure you want to remove this instrument from your watchlist?"
        confirmText="Remove"
        type="danger"
      />

      {/* Named whole-list delete confirmation (replaces window.confirm) */}
      <ConfirmationModal
        isOpen={!!groupToDelete}
        onClose={() => setGroupToDelete(null)}
        onConfirm={() => { if (groupToDelete) handleDeleteGroup(groupToDelete.id); setGroupToDelete(null); }}
        title="Delete watchlist"
        message={`Delete the list “${groupToDelete?.name}” and all ${groupToDelete?.instruments?.length || 0} instrument(s) in it? This cannot be undone.`}
        confirmText="Delete list"
        type="danger"
      />
    </div>
  );
}

export default WatchList;
